// The building on a satellite map, as in xenquote's solar widget (widget/src/solar): zoomed in,
// the building's outline in the brand lime with a pin on it. Where it is comes from
// GET /v1/my-buildings/{orderId}/map, looked up live (Google's terms forbid storing it), with
// the Maps JavaScript browser key, so the key can be rotated without a console build.
// Shown only to the company that ordered the building.
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from "react";
import { useApi } from "../api";

declare global {
  interface Window {
    google?: any;
    __xscMapsReady?: () => void;
    gm_authFailure?: () => void;
  }
}

let loading: Promise<any> | null = null;

/** The global google.maps namespace, loading the script once per page (xenquote's loader). */
function loadMaps(browserKey: string): Promise<any> {
  if (window.google?.maps?.Map) return Promise.resolve(window.google.maps);
  if (loading) return loading;
  loading = new Promise((resolve, reject) => {
    window.__xscMapsReady = () => resolve(window.google.maps);
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(browserKey)}&v=weekly&loading=async&callback=__xscMapsReady`;
    s.async = true;
    s.onerror = () => {
      loading = null; // allow a retry next time
      reject(new Error("The map couldn't load."));
    };
    document.head.appendChild(s);
  });
  return loading;
}

type Api = ReturnType<typeof useApi>;
type MapInfo = Awaited<ReturnType<Api["getBuildingMap"]>>;
type Imagery = Awaited<ReturnType<Api["getRoofImagery"]>>;

// Kept for this browser session (50 minutes, inside the image link's hour), so moving around the
// console and coming back reuses the same lookups and image link: the browser serves the image
// from its cache and Google isn't asked again. The server keeps the imagery for 29 days.
const SESSION_MS = 50 * 60 * 1000;
const remembered = new Map<string, { at: number; value: Promise<unknown> }>();
function once<T>(key: string, load: () => Promise<T>): Promise<T> {
  const hit = remembered.get(key);
  if (hit && Date.now() - hit.at < SESSION_MS) return hit.value as Promise<T>;
  const value = load();
  value.catch(() => remembered.delete(key)); // a failure is retried next time
  remembered.set(key, { at: Date.now(), value });
  return value;
}

/** The roof imagery for an order, fetched once per session and shared (the map and the Roof and panel plan). */
export function roofImagery(api: Api, orderId: string): Promise<Imagery> {
  return once<Imagery>(`imagery:${orderId}`, () => api.getRoofImagery(orderId));
}

/** "2023-09" → "September 2023" */
const monthYear = (ym?: string | null) => (ym ? new Date(`${ym}-01T00:00:00`).toLocaleDateString("en-GB", { month: "long", year: "numeric" }) : null);

export function BuildingMap({ orderId }: { orderId: string }) {
  const api = useApi();
  const div = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<"loading" | "ready" | "none" | "refused">("loading");
  // The high-resolution roof imagery: loading (first view fetches it from Google), shown, or none
  const [roof, setRoof] = useState<{ status: "loading" | "shown" | "hidden" | "none"; date?: string | null; site?: boolean }>({ status: "loading" });

  useEffect(() => {
    let cancelled = false; // StrictMode mounts twice; the first run must not draw
    (async () => {
      try {
        const m = await once<MapInfo>(`map:${orderId}`, () => api.getBuildingMap(orderId));
        // Ask for the roof imagery straight away; it's laid over the map when it arrives
        const imagery = roofImagery(api, orderId);
        if (!m.browserKey || !m.pin) return !cancelled && setState("none");
        // Google calls this when it refuses the key (e.g. this site isn't on the key's allowed
        // websites): say so instead of leaving a blank box
        window.gm_authFailure = () => {
          console.error("Google Maps refused the browser key for", window.location.origin);
          if (!cancelled) setState("refused");
        };
        const maps = await loadMaps(m.browserKey);
        if (cancelled || !div.current) return;
        const map = new maps.Map(div.current, {
          center: m.pin,
          zoom: 19,
          mapTypeId: "satellite",
          tilt: 0,
          disableDefaultUI: true,
          zoomControl: true,
          // Map | Satellite, with Satellite's "Labels" tick box for the hybrid view (roads and names)
          mapTypeControl: true,
          mapTypeControlOptions: { mapTypeIds: ["roadmap", "satellite", "hybrid"], position: maps.ControlPosition.TOP_RIGHT },
          maxZoom: 22, // the roof imagery holds detail past Google's own satellite tiles
          gestureHandling: "cooperative",
          clickableIcons: false,
        });
        // Framed on the building Google matched, but its rectangle is NOT drawn: it can be one corner
        // of the building (Acre Mills). Just the pin until the exact roof outline arrives (#41)
        if (m.box) map.fitBounds({ south: m.box.sw.lat, west: m.box.sw.lng, north: m.box.ne.lat, east: m.box.ne.lng }, 48);
        // The pin: an HTML overlay (xenquote's .xqs-pin), so it can be styled like the widget's
        // It moves onto the traced roof when the roof imagery arrives (its pin is the point deepest
        // inside the roof): the map's own answer can come before the roof was ever traced, and
        // then sits wherever the address was found (Golcar school, XSC-1080)
        class Pin extends maps.OverlayView {
          at = m.pin;
          el = Object.assign(document.createElement("div"), { className: "bmap-pin", innerHTML: "<span>⌂</span>" });
          onAdd() {
            this.getPanes().floatPane.appendChild(this.el);
          }
          draw() {
            const p = this.getProjection()?.fromLatLngToDivPixel(new maps.LatLng(this.at));
            if (p) Object.assign(this.el.style, { left: `${p.x}px`, top: `${p.y}px` });
          }
          onRemove() {
            this.el.remove();
          }
          moveTo(at: { lat: number; lng: number }) {
            this.at = at;
            this.draw();
          }
        }
        const pin = new Pin();
        pin.setMap(map);
        setState("ready");

        // The best roof imagery (~10 cm), straightened onto the map grid by the API: laid over
        // the satellite tiles, under the outline and pin
        imagery
          .then((r) => {
            if (cancelled) return;
            if (!r || !("imageUrl" in r) || !r.imageUrl) return setRoof({ status: "none" });
            if (r.pin) pin.moveTo(r.pin);
            const overlay = new maps.GroundOverlay(r.imageUrl, r.bounds, { clickable: false });
            // The building's exact roof, traced from the roof mask (#41): lime edge, faint lime
            // fill, on every map type. Ground overlays stack in the order they're put on the map,
            // so it goes on AFTER the photo each time, or the photo would hide it
            // The map's version (shadeUrl) also smokes out everything around the roof, so the
            // lime line pops out (Neil, 10 Oct 2026); beyond the photo, the same smoke on the map
            const outlineImage = r.shadeUrl ?? r.outlineUrl;
            const outline = outlineImage ? new maps.GroundOverlay(outlineImage, r.bounds, { clickable: false }) : null;
            if (r.shadeUrl) {
              const { south, west, north, east } = r.bounds;
              const far = 0.2; // degrees: well past anything the map shows here
              new maps.Polygon({
                map,
                paths: [
                  [
                    { lat: south - far, lng: west - far },
                    { lat: north + far, lng: west - far },
                    { lat: north + far, lng: east + far },
                    { lat: south - far, lng: east + far },
                  ],
                  // The photo's own area is left out: the shaded image covers it
                  [
                    { lat: south, lng: west },
                    { lat: south, lng: east },
                    { lat: north, lng: east },
                    { lat: north, lng: west },
                  ],
                ],
                strokeWeight: 0,
                fillColor: "#0a0d16",
                fillOpacity: 170 / 255, // the same smoke as the shaded image (100-api roof_imagery.SHADE)
                clickable: false,
              });
            }
            // The photo is shown on plain Satellite only: it sits above Google's tiles, so on the
            // Map and Labels (hybrid) views it would hide the roads and names around the building
            const sync = () => {
              const onSatellite = map.getMapTypeId() === "satellite";
              overlay.setMap(onSatellite ? map : null);
              outline?.setMap(null);
              outline?.setMap(map);
              setRoof({ status: onSatellite ? "shown" : "hidden", date: r.date, site: r.scope === "site" });
            };
            map.addListener("maptypeid_changed", sync);
            // The record covers several buildings on the site: no single block outlined, the
            // whole site shown instead
            if (r.scope === "site") map.fitBounds(r.bounds, 16);
            sync();
          })
          .catch(() => !cancelled && setRoof({ status: "none" }));
      } catch {
        if (!cancelled) setState("none");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, orderId]);

  if (state === "none") return null;
  if (state === "refused")
    return (
      <div className="bmap bmap-refused muted">
        The map isn't available here yet: this site needs adding to the map key's allowed websites ({window.location.origin}).
      </div>
    );
  return (
    <figure className="bmap">
      <div ref={div} className="bmap-canvas" aria-label="Aerial view of the property" />
      {state === "loading" && <div className="bmap-loading muted">Loading the map…</div>}
      {state === "ready" && roof.status === "loading" && <div className="bmap-badge">Loading high-resolution roof imagery…</div>}
      {roof.site && <div className="bmap-badge">This property covers several buildings on the site: the whole site is shown</div>}
      {/* Google's licence requires this attribution with its solar imagery (the one allowed naming of a source) */}
      {roof.status === "shown" && (
        <figcaption className="bmap-caption">
          Roof imagery{monthYear(roof.date) ? `: ${monthYear(roof.date)}` : ""} · Source: Includes solar data from Google.
        </figcaption>
      )}
    </figure>
  );
}
